import { Helmet } from 'react-helmet-async';
import { H1, H2, Body } from '../components/ui/Typography';
import { EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

export default function Contact() {
  const socialLinks = [
    { name: 'Website', url: 'https://rentfi.io/', description: 'Official website', ariaLabel: 'Visit RentFi\'s official website' },
    { name: 'Knowledge Base', url: 'https://rentfi.crunch.help/', description: 'Official documentation and guides', ariaLabel: 'Access RentFi\'s knowledge base for documentation and guides' },
    { name: 'Merch Store', url: 'https://merch.rentfi.io/', description: 'Official merchandising store', ariaLabel: 'Shop RentFi\'s official merchandise' },
    { name: 'Substack', url: 'https://rentfi.substack.com/welcome', description: 'Official blog and updates', ariaLabel: 'Read RentFi\'s blog posts and updates on Substack' },
    { name: 'X (Twitter)', url: 'https://x.com/RentFi_io', description: 'Official X (Twitter) account', ariaLabel: 'Follow RentFi on X (Twitter)' },
    { name: 'Pinterest', url: 'https://pinterest.com/rentfi/', description: 'Official Pinterest profile', ariaLabel: 'Follow RentFi on Pinterest' },
    { name: 'LinkedIn', url: 'https://linkedin.com/company/rentfi-io', description: 'Official LinkedIn company page', ariaLabel: 'Connect with RentFi on LinkedIn' },
    { name: 'YouTube', url: 'https://youtube.com/@RentFi', description: 'Official YouTube channel', ariaLabel: 'Subscribe to RentFi\'s YouTube channel' },
    { name: 'Facebook', url: 'https://facebook.com/profile.php?id=61572318017380', description: 'Official Facebook page', ariaLabel: 'Follow RentFi on Facebook' },
    { name: 'Instagram', url: 'https://instagram.com/rentfi_io', description: 'Official Instagram profile', ariaLabel: 'Follow RentFi on Instagram' },
    { name: 'TikTok', url: 'https://tiktok.com/@rentfi_io', description: 'Official TikTok account', ariaLabel: 'Follow RentFi on TikTok' },
    { name: 'Trustpilot', url: 'https://www.trustpilot.com/review/rentfi.io', description: 'Official Trustpilot', ariaLabel: 'Read and write RentFi reviews on Trustpilot' }
  ];

  return (
    <>
      <Helmet>
        <title>RentFi | Contact Us</title>
        <meta 
          name="description" 
          content="Get in touch with RentFi's team. Find our official contact information and verified social media channels to stay connected and protected from scams." 
        />
        <meta 
          name="keywords" 
          content="RentFi contact, official channels, social media, customer support" 
        />
        <link rel="canonical" href="https://rentfi.io/contact" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <H1 className="text-primary mb-4">Contact Us</H1>
            <Body className="text-gray-600 mb-8">
              Have questions about RentFi? Our team is here to help. For the fastest response, please reach out through our official channels below.
            </Body>

            {/* Primary Contact Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* Email Support */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <EnvelopeIcon className="w-6 h-6 text-secondary mt-1" />
                  <div>
                    <H2 className="text-gray-900 text-[22px] leading-[36px] mb-2">Email Support</H2>
                    <Body className="text-gray-600 mb-4">
                      For all inquiries and questions about our platform, please contact our support team at:
                    </Body>
                    <a 
                      href="mailto:support@rentfi.helpcrunch-mail.com"
                      className="text-secondary hover:text-secondary-dark"
                      aria-label="Send email to RentFi support team"
                    >
                      support@rentfi.helpcrunch-mail.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Telegram Community */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <PaperAirplaneIcon className="w-6 h-6 text-secondary mt-1 rotate-45" />
                  <div>
                    <H2 className="text-gray-900 text-[22px] leading-[36px] mb-2">Telegram Community</H2>
                    <Body className="text-gray-600 mb-4">
                      Join our vibrant Telegram community for real-time updates, discussions, and peer support:
                    </Body>
                    <a 
                      href="https://t.me/rentfi_io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:text-secondary-dark"
                      aria-label="Join RentFi's Telegram community for updates and discussions"
                    >
                      t.me/rentfi_io
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Official Channels Warning */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">Official Channels</H2>
              <div className="bg-secondary/10 rounded-lg p-6 mb-6">
                <Body className="text-gray-900 font-semibold mb-2">⚠️ Important Security Notice</Body>
                <Body className="text-gray-600">
                  To protect yourself from scams, please only use these verified official channels to contact RentFi. Our team will never ask for your private keys or send you direct messages first. Always verify URLs and social media handles match exactly with those listed below.
                </Body>
              </div>
            </div>

            {/* Official Links */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label={link.ariaLabel}
                >
                  <H2 className="text-[18px] leading-[28px] text-gray-900 mb-1">{link.name}</H2>
                  <Body className="text-gray-600 text-sm">{link.description}</Body>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}